import React from 'react';
import { View, StyleSheet } from 'react-native';

import { ErrorState } from './ErrorState';
import { createPrefixedLogger } from '@utils/logger';
import { captureRenderError } from '@utils/crashReporter';

const log = createPrefixedLogger('ErrorBoundary');

// Re-export so existing barrel consumers keep working.
export { registerCrashReporter } from '@utils/crashReporter';

interface Props {
  children: React.ReactNode;
  // Optional: swap the fallback UI. Receives the error and a reset callback.
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    log.error('Uncaught render error', error, info.componentStack);
    captureRenderError(error, info.componentStack ?? undefined);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <View style={styles.container}>
        <ErrorState
          title="Something went wrong"
          message="An unexpected error interrupted the app. Try again, or restart if the problem persists."
          onRetry={this.reset}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
